// Pure job-building logic for the offline pre-warm (offlinePreload.ts).
// Kept import-free so the node --test suite can exercise it without axios.

export interface PreloadUnit { id: string; condoId: string | null; }
export interface PreloadInput {
  units: PreloadUnit[];
  condoIds: string[];
  /** Active unit id, if any — its jobs are queued first. */
  priorityUnitId?: string | null;
  /** Diagnostic label: 'boot' | 'reconnect' | 'contexts-changed'. */
  reason: string;
}

export function fingerprintOf(input: PreloadInput): string {
  const units = input.units.map((u) => `${u.id}${u.condoId ? `@${u.condoId}` : ''}`).sort();
  const condos = [...input.condoIds].sort();
  return `u:${units.join(',')}|c:${condos.join(',')}`;
}

/** Per-unit home endpoints — every list the Hogar menu modules render from. */
export const UNIT_HOME_PATHS: Array<{ path: string; label: string }> = [
  { path: '/home/tasks', label: 'tasks' },
  { path: '/home/shopping', label: 'shopping' },
  { path: '/home/shopping/sort-order', label: 'shopping-sort' },
  { path: '/home/shopping/custom-lists', label: 'shopping-lists' },
  { path: '/home/budget', label: 'budget' },
  { path: '/home/vehicles', label: 'vehicles' },
  { path: '/home/pets', label: 'pets' },
  { path: '/home/equipment', label: 'equipment' },
  { path: '/home/equipment/reminders', label: 'equipment-reminders' },
];

/**
 * Per-unit non-/home endpoints the home pages fetch on mount. HomeTasks loads
 * the unit detail (residents) for the "Asignar a..." dropdown; offline it was
 * the one remaining 503 on an otherwise cache-served page.
 */
const UNIT_DETAIL_PATHS: Array<{ path: (unitId: string) => string; label: string }> = [
  { path: (unitId) => `/units/${unitId}`, label: 'unit-detail' },
];

export interface PreloadJob { key: string; run: () => Promise<unknown>; }

/** Pure job builder — exported for tests. `get` is the axios-style getter. */
export function buildPreloadJobs(
  input: PreloadInput,
  get: (url: string, config?: { params?: Record<string, string | undefined> }) => Promise<unknown> = (url) => { throw new Error(`offlinePreloadJobs: no getter wired for ${url}`); },
): PreloadJob[] {
  const jobs: PreloadJob[] = [];
  const seenEntitlementPairs = new Set<string>();
  const pushEntitlements = (condoId?: string, unitId?: string) => {
    const key = `entitlements:${condoId || 'none'}:${unitId || 'none'}`;
    if (seenEntitlementPairs.has(key)) return;
    seenEntitlementPairs.add(key);
    const params: Record<string, string> = {};
    if (condoId) params.condoId = condoId;
    if (unitId) params.unitId = unitId;
    jobs.push({ key, run: () => get('/entitlements/me', { params: Object.keys(params).length ? params : undefined }) });
  };
  for (const unit of input.units) {
    for (const { path, label } of UNIT_HOME_PATHS) {
      jobs.push({ key: `${label}:${unit.id}`, run: () => get(path, { params: { unitId: unit.id } }) });
    }
    for (const { path, label } of UNIT_DETAIL_PATHS) {
      jobs.push({ key: `${label}:${unit.id}`, run: () => get(path(unit.id)) });
    }
    pushEntitlements(unit.condoId || undefined, unit.id);
  }
  for (const condoId of input.condoIds) {
    pushEntitlements(condoId);
    jobs.push({ key: `permissions:${condoId}`, run: () => get('/me/permissions', { params: { condoId } }) });
  }
  // Personal/global lens keys (usePermissions '__global__', entitlements
  // 'none:none'; 'none:<standaloneUnitId>' is covered by the unit loop).
  pushEntitlements();
  jobs.push({ key: 'permissions:__global__', run: () => get('/me/permissions') });
  // Active context first so its cache entries are the freshest.
  if (input.priorityUnitId) {
    const prio = jobs.filter((j) => j.key.endsWith(`:${input.priorityUnitId}`) || j.key.startsWith('entitlements:'));
    const rest = jobs.filter((j) => !prio.includes(j));
    return [...prio, ...rest];
  }
  return jobs;
}
